

import React from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from './ui/button';
import { Loader2, AlertTriangle, Download, FileText } from 'lucide-react';
import RobotChatIcon from '@/components/RobotChatIcon';
import CoverLetterIcon from '@/components/CoverLetterIcon';
import RecruiterLensIcon from '@/components/RecruiterLensIcon';
import { useToast } from '@/hooks/use-toast';
import jsPDF from 'jspdf';


interface ResultsModalContentProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  error: string | null;
  analysisResult: string | null;
  action: string | null;
}

const actionToTitleMap: Record<string, string> = {
  critique: "Resume Critique",
  coverletter: "Cover Letter",
  recruiterview: "Recruiter's Perspective",
  fridayrescue: "Resume Rescue",
};

export const ResultsModalContent: React.FC<ResultsModalContentProps> = ({
  isOpen,
  onClose,
  isLoading,
  error,
  analysisResult,
  action,
}) => {
  const { toast } = useToast();
  const title = action ? actionToTitleMap[action] : 'Analysis Results';
  
  const getActionIcon = () => {
    if (action === "critique") return RobotChatIcon;
    if (action === "coverletter") return CoverLetterIcon;
    if (action === "recruiterview") return RecruiterLensIcon;
    return FileText;
  };

  const ActionIcon = getActionIcon();
  if (!isOpen) return null;

const handleDownload = async () => {
  if (!analysisResult || analysisResult.trim().length === 0) {
    toast({
      variant: "destructive",
      title: "Download Error",
      description: "No content available to download.",
    });
    return;
  }

  try {
    const API_URL = import.meta.env.VITE_API_URL;

    if (!API_URL) {
      console.error("VITE_API_URL not set in .env");
      return;
    }

    const resumeId = localStorage.getItem("resumeId");

    if (!resumeId) {
      toast({
        variant: "destructive",
        title: "Resume Missing",
        description: "Please analyze resume again.",
      });
      return;
    }

    // 🔥 TRACK DOWNLOAD
    await fetch(`${API_URL}/api/resume-download-click`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Download-Click": "true"
      },
      body: JSON.stringify({
        resumeId,
        mode: action
      })
    });

  } catch (err) {
    console.error("Download tracking failed:", err);
  }

  toast({
    title: "Preparing Download",
    description: "Generating PDF, please wait...",
  });

  try {
    const cleanContent = analysisResult.replace(/\*\*/g, '');
    const pdf = new jsPDF();

    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text(title, 20, 30);

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');

    const maxWidth = 170;
    const lineHeight = 7;
    let yPosition = 50;

    const paragraphs = cleanContent.split('\n').filter(line => line.trim().length > 0);

    paragraphs.forEach(paragraph => {
      const lines = pdf.splitTextToSize(paragraph, maxWidth);

      if (yPosition + (lines.length * lineHeight) > 280) {
        pdf.addPage();
        yPosition = 20;
      }

      lines.forEach(line => {
        pdf.text(line, 20, yPosition);
        yPosition += lineHeight;
      });

      yPosition += 3;
    });

    pdf.save(`vTech_resume_analysis_${action ?? 'document'}.pdf`);

    toast({
      title: "Download Started",
      description: `Your ${title} PDF is downloading.`,
    });

  } catch (err) {
    console.error("PDF generation error:", err);
    toast({
      variant: "destructive",
      title: "PDF Generation Failed",
      description: "There was an error creating the PDF file. Please try again.",
    });
  }
};


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col bg-gradient-to-br from-background to-background/95 backdrop-blur-sm border border-border/50">
        <DialogHeader className="pb-4 border-b border-border/50">
                     <DialogTitle className="text-2xl font-bold text-foreground flex items-center space-x-3">
             <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
               <ActionIcon className="w-5 h-5 text-primary-foreground" />
             </div>
             <span>{title}</span>
           </DialogTitle>
          <DialogDescription className="text-muted-foreground text-base">
            AI-powered analysis and recommendations for your resume
          </DialogDescription>
        </DialogHeader>
        <div id="analysis-results-content-wrapper" className="flex-grow min-h-0">
          <ScrollArea className="h-full pr-6">
            <div
              id="analysis-results-content"
              className="py-6 px-4 bg-card/30 rounded-lg border border-border/30 m-2 analysis-content"
            >
              {isLoading && (
                <div className="flex flex-col items-center justify-center h-64">
                  <div className="relative">
                    <Loader2 className="h-16 w-16 animate-spin text-primary" />
                    <div className="absolute inset-0 rounded-full border-4 border-primary/20"></div>
                  </div>
                  <p className="mt-6 text-lg font-medium text-foreground">Analyzing Your Resume</p>
                  <p className="mt-2 text-muted-foreground text-center max-w-md">
                    Our AI is carefully reviewing your resume and generating personalized recommendations...
                  </p>
                </div>
              )}
              {error && (
                <div className="flex flex-col items-center justify-center text-center h-64">
                  <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                    <AlertTriangle className="h-8 w-8 text-destructive" />
                  </div>
                  <h3 className="text-lg font-semibold text-destructive mb-2">Analysis Failed</h3>
                  <p className="text-muted-foreground max-w-md">{error}</p>
                  <Button 
                    onClick={onClose} 
                    variant="outline" 
                    className="mt-4"
                  >
                    Try Again
                  </Button>
                </div>
              )}
              {analysisResult && renderMarkdown(analysisResult)}
            </div>
          </ScrollArea>
        </div>
        {!isLoading && !error && analysisResult && (
          <DialogFooter className="pt-4 border-t border-border/50 bg-gradient-to-r from-background/50 to-background/30">
            <div className="flex items-center justify-between w-full">
              <p className="text-sm text-muted-foreground">
                💡 Tip: Download this analysis to keep for future reference
              </p>
              <Button onClick={handleDownload} className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <Download className="mr-2 h-4 w-4" />
                Download
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ResultsModalContent;

// Helper for safe unique keys
const generateHash = (input: string) => {
  try {
    // Simple hash function for browser environment
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  } catch {
    return `fallback-${input.slice(0, 10).replace(/\s+/g, '')}`;
  }
};

// Enhanced markdown renderer with professional styling
const renderMarkdown = (markdownText: string | null) => {
  if (!markdownText) return null;

  const blocks = markdownText.split(/\n{2,}/);

  return blocks.map((block) => {
    block = block.trim();
    if (!block) return null;

    const blockKey = generateHash(block);
    const lines = block.split('\n').filter(Boolean);

    // Handle main title
    if (lines.length === 1 && lines[0].toLowerCase().includes('resume critique')) {
      return (
        <div key={`title-${blockKey}`} className="text-center mb-8">
          <h1 className="text-2xl font-bold text-primary mb-2">
            {lines[0].replace(/^#+\s*/, '')}
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full"></div>
        </div>
      );
    }

    // Handle section headers
    if (lines.length === 1 && block.startsWith('#')) {
      const headerText = lines[0].replace(/^#+\s*/, '');
      return (
        <div key={`header-${blockKey}`} className="mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center">
            <div className="w-2 h-6 bg-primary rounded-full mr-3"></div>
            {headerText}
          </h2>
          <div className="w-full h-px bg-border"></div>
        </div>
      );
    }

    // Handle bullet points and lists
    if (lines.some(line => line.trim().startsWith('•') || line.trim().startsWith('-') || line.trim().startsWith('*'))) {
      return (
        <div key={`list-${blockKey}`} className="mb-6 space-y-2">
          {lines.map((line, index) => {
            const trimmedLine = line.trim();
            if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-') || trimmedLine.startsWith('*')) {
              const content = trimmedLine.replace(/^[•\-*]\s*/, '');
              return (
                <div key={`list-item-${blockKey}-${index}`} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-accent rounded-full mt-2.5 flex-shrink-0"></div>
                  <div className="flex-1">
                    <p className="text-foreground leading-relaxed">{content}</p>
                  </div>
                </div>
              );
            } else if (trimmedLine) {
              return (
                <div key={`list-text-${blockKey}-${index}`} className="ml-5">
                  <p className="text-foreground leading-relaxed">{trimmedLine}</p>
                </div>
              );
            }
            return null;
          })}
        </div>
      );
    }

    // Handle numbered lists
    if (lines.some(line => /^\d+\./.test(line.trim()))) {
      return (
        <div key={`numbered-list-${blockKey}`} className="mb-6 space-y-2">
          {lines.map((line, index) => {
            const trimmedLine = line.trim();
            const match = trimmedLine.match(/^(\d+)\.\s*(.*)/);
            if (match) {
              const [, number, content] = match;
              return (
                <div key={`numbered-item-${blockKey}-${index}`} className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full text-xs font-semibold flex items-center justify-center flex-shrink-0">
                    {number}
                  </div>
                  <div className="flex-1">
                    <p className="text-foreground leading-relaxed">{content}</p>
                  </div>
                </div>
              );
            } else if (trimmedLine) {
              return (
                <div key={`numbered-text-${blockKey}-${index}`} className="ml-9">
                  <p className="text-foreground leading-relaxed">{trimmedLine}</p>
                </div>
              );
            }
            return null;
          })}
        </div>
      );
    }

    // Handle emphasis and bold text
    const renderLine = (line: string) => {
      const parts = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
      return parts.map((part, partIndex) => {
        if (/^\*\*[^*]+\*\*$/.test(part)) {
          return (
            <span key={`bold-${partIndex}`} className="font-semibold text-primary">
              {part.slice(2, -2)}
            </span>
          );
        }
        return part;
      });
    };

    // Handle regular paragraphs
    return (
      <div key={`paragraph-${blockKey}`} className="mb-6 space-y-3">
        {lines.map((line, index) => {
          const trimmedLine = line.trim();
          if (!trimmedLine) return null;
          
          return (
            <p key={`p-${blockKey}-${index}`} className="text-foreground leading-relaxed">
              {renderLine(trimmedLine)}
            </p>
          );
        })}
      </div>
    );
  });
};
